import React from 'react'
import { useHydrated } from 'remix-utils/use-hydrated'
import chevronImage from '#app/assets/chevron.png?url'

interface InstructionsProps {
	chevronImage: string
}

interface ElevationChartInstructionsProps {
	isTouchDevice: boolean
}

interface MapInstructionsProps {
	isTouchDevice: boolean
	largeScreen: boolean
}

interface SectionProps {
	title: string
	children: React.ReactNode
}

interface InstructionListProps {
	children: React.ReactNode
}

interface InstructionProps {
	label: string
	text: React.ReactNode
}

interface ParagraphProps {
	children: React.ReactNode
}

const TrailHelp: React.FC = () => {
	const hydrated = useHydrated()
	const isTouchDevice =
		hydrated && ('ontouchstart' in window || navigator.maxTouchPoints > 0)

	const SmallInstructions = isTouchDevice
		? TouchInstructionsSmallScreen
		: ComputerInstructionsSmallScreen

	const LargeInstructions = isTouchDevice
		? TouchInstructionsLargeScreen
		: ComputerInstructionsLargeScreen

	return (
		<div className="m-4">
			<div className="hidden lg:block">
				<LargeInstructions chevronImage={chevronImage} />
			</div>

			<div className="block lg:hidden">
				<SmallInstructions chevronImage={chevronImage} />
			</div>
		</div>
	)
}

const TouchInstructionsLargeScreen: React.FC<InstructionsProps> = ({
	chevronImage,
}) => (
	<>
		<Section title="How to Use the 360° Image">
			<InstructionList>
				<Instruction
					label="Look Around:"
					text="Swipe with your finger to move the camera."
				/>
				<Instruction
					label="Move Forward:"
					text={
						<>
							Find the chevron icon{' '}
							<img
								src={chevronImage}
								alt="Chevron Icon"
								className="mx-2 inline-block h-5 w-5 align-middle"
							/>{' '}
							pointing in the direction you would like to go. You may need to
							look around to find it. Tap on the chevron to advance along the
							trail.
						</>
					}
				/>
				<Instruction
					label="Zoom:"
					text="Pinch with two fingers to zoom in or out."
				/>
			</InstructionList>
		</Section>

		<ElevationChartInstructions isTouchDevice={true} />
		<MapInstructions isTouchDevice={true} largeScreen={true} />
	</>
)

const ComputerInstructionsLargeScreen: React.FC<InstructionsProps> = ({
	chevronImage,
}) => (
	<>
		<Section title="How to Use the 360° Image">
			<InstructionList>
				<Instruction
					label="Look Around:"
					text="Click and drag to move the camera."
				/>
				<Instruction
					label="Move Forward:"
					text={
						<>
							Find the chevron icon{' '}
							<img
								src={chevronImage}
								alt="Chevron Icon"
								className="mx-2 inline-block h-5 w-5 align-middle"
							/>{' '}
							pointing in the direction you would like to go. You may need to
							look around to find it. Click on the chevron to advance along the
							trail.
						</>
					}
				/>
				<Instruction
					label="Zoom:"
					text="Use the scroll wheel to zoom in or out."
				/>
			</InstructionList>
			<Paragraph>
				<span className="font-semibold">Maximize View:</span> For a more
				immersive experience, click the fullscreen button in the top-right
				corner of the image.
			</Paragraph>
		</Section>

		<ElevationChartInstructions isTouchDevice={false} />
		<MapInstructions isTouchDevice={false} largeScreen={true} />
	</>
)

const TouchInstructionsSmallScreen: React.FC<InstructionsProps> = ({
	chevronImage,
}) => (
	<>
		<Section title="How to Use the Tabs">
			<InstructionList>
				<Instruction
					label="Navigate Between Views:"
					text="Tap on the tabs at the top of the screen to switch between Description, Image, Elevation, Map, and Help."
				/>
			</InstructionList>
		</Section>

		<Section title="How to Use the 360° Image">
			<InstructionList>
				<Instruction
					label="Look Around:"
					text="Swipe with your finger to move the camera."
				/>
				<Instruction
					label="Move Forward:"
					text={
						<>
							Find the chevron icon{' '}
							<img
								src={chevronImage}
								alt="Chevron Icon"
								className="mx-2 inline-block h-5 w-5 align-middle"
							/>{' '}
							pointing in the direction you would like to go. You may need to
							look around to find it. Tap on the chevron to advance along the
							trail.
						</>
					}
				/>
				<Instruction
					label="Zoom:"
					text="Pinch with two fingers to zoom in or out."
				/>
			</InstructionList>
		</Section>

		<ElevationChartInstructions isTouchDevice={true} />
		<MapInstructions isTouchDevice={true} largeScreen={false} />
	</>
)

const ComputerInstructionsSmallScreen: React.FC<InstructionsProps> = ({
	chevronImage,
}) => (
	<>
		<Section title="How to Use the Tabs">
			<InstructionList>
				<Instruction
					label="Navigate Between Views:"
					text="Click on the tabs at the bottom of the screen to switch between Description, Image, Elevation, Map, and Help."
				/>
			</InstructionList>
		</Section>

		<Section title="How to Use the 360° Image">
			<InstructionList>
				<Instruction
					label="Look Around:"
					text="Click and drag to move the camera."
				/>
				<Instruction
					label="Move Forward:"
					text={
						<>
							Find the chevron icon{' '}
							<img
								src={chevronImage}
								alt="Chevron Icon"
								className="mx-2 inline-block h-5 w-5 align-middle"
							/>{' '}
							pointing in the direction you would like to go. You may need to
							look around to find it. Click on the chevron to advance along the
							trail.
						</>
					}
				/>
				<Instruction
					label="Zoom:"
					text="Use the scroll wheel to zoom in or out."
				/>
			</InstructionList>
		</Section>

		<ElevationChartInstructions isTouchDevice={false} />
		<MapInstructions isTouchDevice={false} largeScreen={false} />
	</>
)

const Section: React.FC<SectionProps> = ({ title, children }) => (
	<div className="mt-6">
		<h5 className="text-xl font-semibold">{title}</h5>
		{children}
	</div>
)

const InstructionList: React.FC<InstructionListProps> = ({ children }) => (
	<ul className="mt-2 list-inside list-disc space-y-2">{children}</ul>
)

const Instruction: React.FC<InstructionProps> = ({ label, text }) => (
	<li>
		<span className="font-semibold">{label}</span> {text}
	</li>
)

const Paragraph: React.FC<ParagraphProps> = ({ children }) => (
	<p className="mt-2">{children}</p>
)

const ElevationChartInstructions: React.FC<ElevationChartInstructionsProps> = ({
	isTouchDevice,
}) => (
	<Section title="How to Use the Elevation Chart">
		<Paragraph>
			The <span className="font-semibold text-green-600">green line</span>{' '}
			represents the trail's elevation profile.
		</Paragraph>
		<Paragraph>
			The <span className="font-semibold text-blue-600">blue line</span>{' '}
			represents the current location of the 360° image.
		</Paragraph>
		<Paragraph>
			{isTouchDevice
				? 'Tap on any point of the chart to view the 360° image at that location.'
				: 'Hover over the chart to see elevation, grade, distance, and thumbnail image. Click on any point to view the 360° image at that location.'}
		</Paragraph>
	</Section>
)

const MapInstructions: React.FC<MapInstructionsProps> = ({
	isTouchDevice,
	largeScreen,
}) => (
	<Section title="How to Use the Map">
		<Paragraph>
			<span className="font-semibold">Map Symbols:</span>
		</Paragraph>
		<ul className="mt-2 list-inside list-disc space-y-1">
			<li>
				<span className="font-semibold text-blue-600">Blue Line:</span> The
				trail path.
			</li>
			<li>
				<span className="font-semibold">Panorama Icon:</span> Your current
				location on the trail.
			</li>
			<li>
				<span className="font-semibold">Home Icon:</span> The trail's starting
				point.
			</li>
			<li>
				<span className="font-semibold">Flag Icon:</span> The trail's endpoint.
			</li>
		</ul>
		<Paragraph>
			{isTouchDevice
				? 'Touch and drag to move the map. Pinch to zoom. Tap on the trail to view the 360° image at that location.'
				: 'Click and drag to move the map. Use the scroll wheel to zoom. Click on the trail to view the 360° image at that location.'}
		</Paragraph>
		{largeScreen && !isTouchDevice && (
			<Paragraph>
				<span className="font-semibold">Maximize View:</span> Use the arrow
				buttons to adjust the map view.
			</Paragraph>
		)}
	</Section>
)

export default TrailHelp
